import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, Notice, PageTitle, Spinner } from "../../ui/index.js";
import { fmtDateTime, useCabinet } from "../cabinet/index.js";

const ERROR_REASONS = ["denied", "invalid_state", "no_account", "permissions", "account_taken", "subscribe_failed", "config", "meta_error"] as const;
type ErrorReason = (typeof ERROR_REASONS)[number];
const isReason = (v: string | null): v is ErrorReason => ERROR_REASONS.includes(v as ErrorReason);

/**
 * Подключение Instagram (`/s/:salonId/settings/instagram`, владелец): состояние аккаунта, «Подключить» (переход
 * на диалог входа Meta: логин Instagram в браузере, выбор бизнес-портфолио и аккаунта) и «Отключить».
 * После возврата из Meta сервер редиректит сюда с `?result=`. Токен Unified Login обычно бессрочный («без срока»).
 */
export function InstagramSettingsPage() {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const [params, setParams] = useSearchParams();
  const status = useQuery(trpc.instagram.status.queryOptions({ salonId: cab.salonId }));
  const start = useMutation(trpc.instagram.connectStart.mutationOptions({ onSuccess: ({ url }) => window.location.assign(url) }));
  const disconnect = useMutation(
    trpc.instagram.disconnect.mutationOptions({ onSuccess: () => queryClient.invalidateQueries(trpc.instagram.status.pathFilter()) }),
  );

  const result = params.get("result");
  const reason = params.get("reason");
  // Вернулись из Instagram: состояние могло измениться.
  useEffect(() => {
    if (result) void queryClient.invalidateQueries(trpc.instagram.status.pathFilter());
  }, [result]);
  const dismiss = () => setParams({}, { replace: true });

  if (status.isPending) return <Spinner />;
  if (status.isError) return <Notice kind="error">{t("common.error")}</Notice>;
  const s = status.data;
  const account = s.account;
  const connected = account?.status === "connected";
  const fmt = (d: Date | null) => (d ? fmtDateTime(d, cab.timezone, i18n.language) : "–");

  return (
    <div>
      <PageTitle>{t("instagram.title")}</PageTitle>
      <p className="mb-4 text-sm text-slate-600 lg:max-w-3xl">{t("instagram.what")}</p>

      {result === "connected" && (
        <div className="mb-4">
          <Notice kind="success">{t("instagram.connectedNow")}</Notice>
        </div>
      )}
      {result === "error" && (
        <div className="mb-4">
          <Notice kind="error">{t(`instagram.errors.${isReason(reason) ? reason : "meta_error"}`)}</Notice>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        <Card className="space-y-3">
          {!s.configured ? (
            <Notice>{t("instagram.notConfigured")}</Notice>
          ) : (
            <>
              <div className="font-medium">
                {connected
                  ? t("instagram.connectedAs", { username: account?.username ?? "" })
                  : account?.status === "error"
                    ? t("instagram.statusError")
                    : t("instagram.notConnected")}
              </div>
              {account && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm text-slate-600">
                  <dt>{t("instagram.tokenUntil")}</dt>
                  <dd>{account.tokenExpiresAt ? fmt(account.tokenExpiresAt) : t("instagram.tokenForever")}</dd>
                  <dt>{t("instagram.lastWebhook")}</dt>
                  <dd>{fmt(account.lastWebhookAt)}</dd>
                  {account.lastError && (
                    <>
                      <dt>{t("instagram.lastError")}</dt>
                      <dd className="break-words text-red-700">{account.lastError}</dd>
                    </>
                  )}
                </dl>
              )}
              {(start.isError || disconnect.isError) && <Notice kind="error">{t("common.error")}</Notice>}
              {!connected && (
                <Button
                  className="w-full lg:w-auto"
                  disabled={start.isPending}
                  onClick={() => {
                    dismiss();
                    start.mutate({ salonId: cab.salonId });
                  }}
                >
                  {account ? t("instagram.reconnect") : t("instagram.connect")}
                </Button>
              )}
              {(connected || account?.status === "error") && (
                <Button
                  variant="secondary"
                  className="w-full lg:w-auto"
                  disabled={disconnect.isPending}
                  onClick={() => {
                    dismiss();
                    disconnect.mutate({ salonId: cab.salonId });
                  }}
                >
                  {t("instagram.disconnect")}
                </Button>
              )}
            </>
          )}
        </Card>

        <Card className="mt-4 space-y-2 text-sm text-slate-700 lg:mt-0">
          <div className="font-medium">{t("instagram.requirementsTitle")}</div>
          <ul className="list-disc space-y-1 pl-5">
            <li>{t("instagram.reqProfessional")}</li>
            <li>{t("instagram.reqWindow")}</li>
            <li>{t("instagram.reqEcho")}</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
